"use client";
/*
  HOME_BISECT FLAGS (toggle to isolate blockers on /customer/home):
    NEXT_PUBLIC_HOME_BISECT_MAP (default 1)
    NEXT_PUBLIC_HOME_BISECT_HOME_AUDIT (default 1)
    NEXT_PUBLIC_HOME_BISECT_PD_TRACER (default 1)
    NEXT_PUBLIC_HOME_BISECT_SAFE_NAV (default 0)
    NEXT_PUBLIC_HOME_BISECT_TILE_DIAG (default 1)
  Protocol: build prod, toggle one flag to 0 (or SAFE_NAV to 1), rebuild, and observe whether anchor clicks still get defaultPrevented. If a flag fixes it, the associated module is the culprit.
*/

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import React from "react";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { useAuth } from "@/components/AuthProvider";
import YourBarrioLoader from "@/components/ui/YourBarrioLoader";
import { useTheme } from "@/components/ThemeProvider";
import dynamic from "next/dynamic";
import { resolveListingCardImageUrl } from "@/lib/listingPhotos";
import FastImage from "@/components/FastImage";
import {
  getAvailabilityBadgeStyle,
  normalizeInventory,
  sortListingsByAvailability,
} from "@/lib/inventory";
import { BUSINESS_CATEGORIES, normalizeCategoryName } from "@/lib/businessCategories";
import { logDataDiag } from "@/lib/dataDiagnostics";
import TrendingListingsSection from "@/components/home/TrendingListingsSection";
import {
  SellerCTASection,
} from "@/components/home/HomeDiscoverySections";
import HomepageEditorialSections from "@/components/homepage/HomepageEditorialSections";
import HomepageAvailabilityNotice from "@/components/homepage/HomepageAvailabilityNotice";
import BusinessStoriesRow from "@/components/stories/BusinessStoriesRow";
import { hasStoryDiscoveryLocationChanged } from "@/lib/stories/businessStories";
import { useLocation } from "@/components/location/LocationProvider";
import FeedbackSection from "@/components/browse/FeedbackSection";
import { getListingCategoryLabel } from "@/lib/taxonomy/compat";
import { calculateListingPricing } from "@/lib/pricing";
import { getDefaultLaunchLocation, withLocationHref } from "@/lib/location";

const HomeGuard = dynamic(() => import("@/components/debug/HomeGuard"), { ssr: false });

function formatListingDisplayPrice(item) {
  const finalPriceCents = Number(item?.finalPriceCents);
  const cents =
    Number.isFinite(finalPriceCents) && finalPriceCents > 0
      ? finalPriceCents
      : calculateListingPricing(item?.price).finalPriceCents;
  return cents > 0 ? `$${(cents / 100).toFixed(2)}` : null;
}
function HomeGuardFallback() {
  const { theme, hydrated } = useTheme();
  const isLight = hydrated ? theme === "light" : true;
  const textBase = isLight ? "text-slate-900" : "text-white";
  const textMuted = isLight ? "text-slate-600" : "text-white/70";

  return (
    <div className={`min-h-screen ${textBase} relative px-6 pt-3`}>
      <div className="absolute inset-0 -z-10 overflow-hidden pointer-events-none">
        <div className="absolute inset-0 bg-[#05010d] pointer-events-none" />
        <div className="absolute inset-0 bg-gradient-to-b from-purple-900/40 via-fuchsia-900/30 to-black pointer-events-none" />
        <div className="absolute -top-32 -left-24 h-[420px] w-[420px] rounded-full bg-purple-600/30 blur-[120px] pointer-events-none" />
        <div className="absolute top-40 -right-24 h-[480px] w-[480px] rounded-full bg-pink-500/30 blur-[120px] pointer-events-none" />
      </div>
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-3">
          <YourBarrioLoader size="md" className="mx-auto text-white/80" />
          <p className={`text-lg ${textMuted}`}>Loading your account...</p>
        </div>
      </div>
    </div>
  );
}
const SafeNavFallback = dynamic(() => import("@/components/nav/SafeNavFallback"), { ssr: false });
import GuaranteedNavCapture from "@/components/nav/GuaranteedNavCapture";

class CustomerHomeErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: "" };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, message: error?.message || "Something went wrong." };
  }

  componentDidCatch(error, info) {
    console.error("Customer home crashed", error, info);
    void import("@/lib/crashlog")
      .then(({ appendCrashLog }) => {
        appendCrashLog({
          type: "react-error",
          message: error?.message,
          stack: error?.stack,
          info: info?.componentStack,
          route: "/customer/home",
        });
      })
      .catch(() => {});
  }

  render() {
    if (this.state.hasError) {
      const isLight = this.props.isLight ?? true;
      const textBase = isLight ? "text-slate-900" : "text-white";
      const textMuted = isLight ? "text-slate-600" : "text-white/70";
      return (
        <div className={`min-h-screen flex items-center justify-center px-6 ${textBase}`}>
          <div className="max-w-md w-full space-y-4 text-center bg-white/5 border border-white/10 rounded-2xl p-6">
            <h2 className="text-xl font-semibold">Something went wrong</h2>
            <p className={`text-sm ${textMuted}`}>
              {this.state.message || "The page failed to load. Please try again."}
            </p>
            <button
              type="button"
              onClick={() => {
                this.setState({ hasError: false, message: "" });
                if (typeof window !== "undefined") window.location.reload();
              }}
              className="w-full py-3 rounded-xl font-semibold bg-white text-black hover:bg-white/90 transition"
            >
              Reload
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

function CustomerHomePageInner({
  mode,
  initialListings = [],
  initialStoryGroups = [],
  initialStoryNextCursor = null,
  initialStoryHasMore = false,
  initialCity = null,
  initialRegion = null,
}) {
  const searchParams = useSearchParams();
  const { user, loadingUser } = useAuth();
  const { theme, hydrated } = useTheme();
  const isLight = hydrated ? theme === "light" : true;
  const { location, hasLocation, hydrated: locationHydrated, setLocation } = useLocation();
  const [homeContentHydrated, setHomeContentHydrated] = useState(false);
  const activeCity = String(
    (homeContentHydrated ? location?.city : initialCity) || initialCity || ""
  ).trim();
  // Slightly stronger light-theme tones to preserve contrast on white surfaces.
  const textTone = useMemo(
    () => ({
      base: isLight ? "text-slate-900" : "text-white",
      strong: isLight ? "text-slate-900" : "text-white/90",
      muted: isLight ? "text-slate-700" : "text-white/80",
      soft: isLight ? "text-slate-600" : "text-white/70",
      subtle: isLight ? "text-slate-500" : "text-white/60",
      faint: isLight ? "text-slate-600" : "text-white/50",
      tint: isLight ? "text-slate-700" : "text-white/75",
    }),
    [isLight]
  );
  // Keep the SSR snapshot intact until this client component has hydrated. The
  // location provider can resolve a persisted location before a streamed home
  // segment finishes hydrating; letting that alter these sibling sections used
  // to make the server's listing snapshot differ from the first client tree.
  const [search, setSearch] = useState(() => (searchParams?.get("q") || "").trim());
  const [categoryFilter, setCategoryFilter] = useState(() => {
    const normalizedUrl = normalizeCategoryName(
      (searchParams?.get("category") || "").trim()
    ).toLowerCase();
    return (
      BUSINESS_CATEGORIES.find(
        (category) => normalizeCategoryName(category.name).toLowerCase() === normalizedUrl
      )?.name || "All"
    );
  });
  const showLocationEmpty = homeContentHydrated && locationHydrated && !hasLocation;
  // DEBUG_CLICK_DIAG
  const clickDiagEnabled = process.env.NEXT_PUBLIC_CLICK_DIAG === "1";
  const tileHitTestEnabled = process.env.NEXT_PUBLIC_DEBUG_NAV_PERF === "1";
  const homeBisect = {
    homeAudit: process.env.NEXT_PUBLIC_HOME_BISECT_HOME_AUDIT !== "0",
    pdTracer: process.env.NEXT_PUBLIC_HOME_BISECT_PD_TRACER !== "0",
    safeNav: process.env.NEXT_PUBLIC_HOME_BISECT_SAFE_NAV === "1",
    tileDiag: process.env.NEXT_PUBLIC_HOME_BISECT_TILE_DIAG !== "0",
  };
  const [homeListings, setHomeListings] = useState(() =>
    Array.isArray(initialListings) ? initialListings : []
  );
  const [homeListingsLoading, setHomeListingsLoading] = useState(false);
  const [storyGroups, setStoryGroups] = useState(() =>
    Array.isArray(initialStoryGroups) ? initialStoryGroups : []
  );
  const [storyNextCursor, setStoryNextCursor] = useState(initialStoryNextCursor);
  const [storyHasMore, setStoryHasMore] = useState(Boolean(initialStoryHasMore));
  const [hybridItems, setHybridItems] = useState([]);
  const [hybridItemsLoading, setHybridItemsLoading] = useState(false);
  const [hybridItemsError, setHybridItemsError] = useState(null);
  const homeListingsRequestIdRef = useRef(0);
  const hybridRequestIdRef = useRef(0);
  const isPublicMode = mode === "public";
  const authReady = isPublicMode ? true : !loadingUser || !!user;
  const visibleHomeListings = homeContentHydrated
    ? homeListings
    : Array.isArray(initialListings)
      ? initialListings
      : [];
  const visibleHomeListingsLoading = homeContentHydrated ? homeListingsLoading : false;
  const launchLocation = useMemo(() => getDefaultLaunchLocation(), []);
  const launchListingsHref = useMemo(
    () => withLocationHref("/listings", launchLocation),
    [launchLocation]
  );
  const showCityAvailabilityNotice =
    homeContentHydrated &&
    locationHydrated &&
    hasLocation &&
    !visibleHomeListingsLoading &&
    visibleHomeListings.length === 0;
  const [, setIsVisible] = useState(() =>
    typeof document === "undefined" ? true : !document.hidden
  );
  const logCrashEvent = useCallback((payload) => {
    void import("@/lib/crashlog")
      .then(({ appendCrashLog }) => {
        appendCrashLog({
          type: "customer-home",
          ...payload,
        });
      })
      .catch(() => {});
  }, []);


  useEffect(() => {
    if (typeof document === "undefined") return;
    const handleVisibility = () => setIsVisible(!document.hidden);
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, []);

  useEffect(() => {
    if (!tileHitTestEnabled) return undefined;
    if (typeof document === "undefined") return undefined;
    const handleClickCapture = (event) => {
      try {
        const target = event.target;
        const tile = target?.closest?.('[data-layer="tile"], [data-safe-nav="1"]');
        if (!tile) return;
        const anchor = target?.closest?.("a[href]");
        console.log("[tile-hit-test]", {
          target: target?.tagName,
          currentTarget: event.currentTarget?.tagName,
          anchor: anchor?.getAttribute?.("href") || null,
        });
      } catch {
        /* ignore */
      }
    };
    document.addEventListener("click", handleClickCapture, true);
    return () => document.removeEventListener("click", handleClickCapture, true);
  }, [tileHitTestEnabled]);

  useEffect(() => {
    if (!clickDiagEnabled || !homeBisect.pdTracer) return undefined;
    let active = true;
    let cleanup;
    void import("@/lib/tracePreventDefault")
      .then(({ installPreventDefaultTracer }) => {
        if (!active) return;
        cleanup = installPreventDefaultTracer();
      })
      .catch(() => {});
    return () => {
      active = false;
      if (typeof cleanup === "function") cleanup();
    };
  }, [clickDiagEnabled, homeBisect.pdTracer]);

  useEffect(() => {
    if (!clickDiagEnabled) return undefined;
    void import("@/lib/navInstrumentation")
      .then(({ installHomeNavInstrumentation }) => {
        installHomeNavInstrumentation({ enabled: true });
      })
      .catch(() => {});
    return undefined;
  }, [clickDiagEnabled]);


  useEffect(() => {
    if (!clickDiagEnabled || !homeBisect.homeAudit) return undefined;
    const stringifyNode = (node) => {
      if (!node) return "null";
      if (node === document) return "document";
      if (node === window) return "window";
      if (!node.tagName) return node.nodeName || "unknown";
      const tag = node.tagName.toLowerCase();
      const cls = (node.className || "").toString().trim().split(/\s+/).filter(Boolean).slice(0, 2).join(".");
      return `${tag}${cls ? `.${cls}` : ""}`;
    };
    const audit = (event) => {
      try {
        const { clientX = 0, clientY = 0 } = event;
        const top = document.elementFromPoint(clientX, clientY);
        const path = typeof event.composedPath === "function" ? event.composedPath() : [];
        const anchor = event.target?.closest?.("a[href]");
        console.log("[CLICK_DIAG] HOME_AUDIT", {
          type: event.type,
          phase: "capture",
          defaultPrevented: event.defaultPrevented,
          cancelBubble: event.cancelBubble,
          target: stringifyNode(event.target),
          top: stringifyNode(top),
          anchor: anchor?.getAttribute?.("href") || null,
          path: path.slice(0, 6).map(stringifyNode),
        });
      } catch {
        /* ignore */
      }
    };
    const anchorPrevented = (event) => {
      const a = event.target?.closest?.("a[href]");
      if (!a) return;
      queueMicrotask(() => {
        if (event.defaultPrevented) {
          console.warn("[ANCHOR_PREVENTED]", {
            href: a.getAttribute?.("href"),
            stack: new Error().stack,
          });
        }
      });
    };
    document.addEventListener("click", audit, { capture: true, passive: true });
    document.addEventListener("pointerdown", audit, { capture: true, passive: true });
    document.addEventListener("click", anchorPrevented, { capture: true, passive: true });
    return () => {
      document.removeEventListener("click", audit, { capture: true, passive: true });
      document.removeEventListener("pointerdown", audit, { capture: true, passive: true });
      document.removeEventListener("click", anchorPrevented, { capture: true, passive: true });
    };
  }, [clickDiagEnabled, homeBisect.homeAudit]);
  useEffect(() => {
    if (process.env.NEXT_PUBLIC_DEBUG_NAV_PERF !== "1") return undefined;
    if (typeof window === "undefined" || typeof PerformanceObserver === "undefined") {
      return undefined;
    }
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        console.log("[longtask]", Math.round(entry.duration), "ms", entry);
      }
    });
    try {
      observer.observe({ entryTypes: ["longtask"] });
    } catch {
      return undefined;
    }
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    if (process.env.NEXT_PUBLIC_DEBUG_NAV_PERF !== "1") return undefined;
    if (typeof window === "undefined" || typeof requestAnimationFrame === "undefined") {
      return undefined;
    }
    let rafId = 0;
    let last = performance.now();
      const tick = (now) => {
        const delta = now - last;
        if (delta > 200) {
          console.log("[raf-stall]", Math.round(delta), "ms", new Date().toISOString());
          void import("@/lib/stallRecorder")
            .then(({ dumpStallRecorder }) => {
              dumpStallRecorder("raf-stall");
            })
            .catch(() => {});
        }
        last = now;
        rafId = requestAnimationFrame(tick);
      };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, []);
  const diagTileClick =
    (label, tileId) =>
      (event) => {
        if (!clickDiagEnabled || !homeBisect.tileDiag) return;
        console.log(`[CLICK_DIAG] ${label}`, {
          tileId,
          defaultPrevented: event.defaultPrevented,
          target: event.target?.tagName,
          currentTarget: event.currentTarget?.tagName,
        });
        const hrefAttr = event.currentTarget?.getAttribute?.("href");
        if (hrefAttr) {
          console.log("[CLICK_DIAG] TILE_HREF", { tileId, href: hrefAttr });
        }
        queueMicrotask(() => {
          console.log("[CLICK_DIAG] TILE_POST", { tileId, href: window.location.href });
        });
      };
  const coverFor = (listing) => resolveListingCardImageUrl(listing) || null;

  useEffect(() => {
    setHomeContentHydrated(true);
  }, []);

  useEffect(() => {
    const urlQuery = (searchParams?.get("q") || "").trim();
    const urlCategory = (searchParams?.get("category") || "").trim();
    const normalizedUrl = normalizeCategoryName(urlCategory).toLowerCase();
    const matchedCategory = BUSINESS_CATEGORIES.find(
      (category) =>
        normalizeCategoryName(category.name).toLowerCase() === normalizedUrl
    );
    setSearch(urlQuery);
    setCategoryFilter(matchedCategory?.name || "All");
  }, [searchParams]);

  useEffect(() => {
    setHomeListings(Array.isArray(initialListings) ? initialListings : []);
  }, [initialListings]);

  useEffect(() => {
    setStoryGroups(Array.isArray(initialStoryGroups) ? initialStoryGroups : []);
    setStoryNextCursor(initialStoryNextCursor || null);
    setStoryHasMore(Boolean(initialStoryHasMore));
  }, [initialStoryGroups, initialStoryHasMore, initialStoryNextCursor]);

  useEffect(() => {
    if (!homeContentHydrated) return undefined;
    if (!locationHydrated) return undefined;
    if (!hasLocation) return undefined;

    const locationChanged = hasStoryDiscoveryLocationChanged(
      location,
      { city: initialCity, region: initialRegion }
    );
    const shouldRefreshHomeListings =
      homeListings.length === 0
      || locationChanged;

    if (!shouldRefreshHomeListings) return undefined;

    let isActive = true;
    const requestId = ++homeListingsRequestIdRef.current;
    const controller = new AbortController();

    const loadHomeListings = async () => {
      setHomeListingsLoading(true);
      if (locationChanged) {
        setStoryGroups([]);
        setStoryNextCursor(null);
        setStoryHasMore(false);
      }
      try {
        const params = new URLSearchParams();
        params.set("limit", "20");
        params.set("featured", "1");
        if (location?.city) params.set("city", location.city);
        if (location?.region) params.set("state", location.region);
        if (location?.lat != null) params.set("lat", String(location.lat));
        if (location?.lng != null) params.set("lng", String(location.lng));

        const storyParams = new URLSearchParams(params);
        storyParams.delete("featured");
        storyParams.set("limit", "16");
        const [response, storyResponse] = await Promise.all([
          fetch(`/api/home-listings?${params.toString()}`, {
            signal: controller.signal,
            cache: "no-store",
          }),
          fetch(`/api/stories?${storyParams.toString()}`, {
            signal: controller.signal,
            cache: "no-store",
          }),
        ]);
        const [payload, storyPayload] = await Promise.all([
          response.json().catch(() => ({})),
          storyResponse.json().catch(() => ({})),
        ]);

        if (!isActive || requestId !== homeListingsRequestIdRef.current) return;
        const nextListings = response.ok && Array.isArray(payload?.listings) ? payload.listings : [];
        setHomeListings(nextListings);
        setStoryGroups(
          storyResponse.ok && Array.isArray(storyPayload?.items)
            ? storyPayload.items
            : []
        );
        setStoryNextCursor(storyResponse.ok ? storyPayload?.nextCursor || null : null);
        setStoryHasMore(storyResponse.ok && storyPayload?.hasMore === true);
      } catch (error) {
        if (!isActive || requestId !== homeListingsRequestIdRef.current) return;
        if (error?.name === "AbortError") return;
        setHomeListings([]);
      } finally {
        if (isActive && requestId === homeListingsRequestIdRef.current) {
          setHomeListingsLoading(false);
        }
      }
    };

    void loadHomeListings();

    return () => {
      isActive = false;
      controller.abort();
    };
  }, [
    hasLocation,
    homeListings.length,
    initialCity,
    initialRegion,
    location,
    locationHydrated,
    homeContentHydrated,
  ]);

  const sortedHybridItems = useMemo(
    () => sortListingsByAvailability(hybridItems),
    [hybridItems]
  );
  const browseLaunchListings = useCallback(() => {
    setLocation?.(launchLocation);
  }, [launchLocation, setLocation]);
  useEffect(() => {
    if (!homeContentHydrated) return undefined;
    let isActive = true;
    const requestId = ++hybridRequestIdRef.current;
    const term = search.trim();
    const categoryValue = categoryFilter.trim();

    if (!hasLocation) {
      setHybridItems([]);
      setHybridItemsError("Select a location to search.");
      setHybridItemsLoading(false);
      return undefined;
    }

    if (!term) {
      setHybridItems([]);
      setHybridItemsError(null);
      setHybridItemsLoading(false);
      return undefined;
    }

    const loadHybridItems = async () => {
      setHybridItemsLoading(true);
      setHybridItemsError(null);
      logDataDiag("request:start", { label: "home:hybrid-search", requestId });

      const safe = term.replace(/[%_]/g, "");
      if (!safe) {
        setHybridItems([]);
        setHybridItemsLoading(false);
        return;
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(
        () => controller.abort(new DOMException("Timeout", "AbortError")),
        12000
      );

      try {
        const params = new URLSearchParams();
        params.set("q", safe);
        if (categoryValue && categoryValue !== "All") {
          params.set("category", categoryValue);
        }
        const response = await fetch(`/api/search?${params.toString()}`, {
          signal: controller.signal,
          cache: "no-store",
        });
        const payload = await response.json().catch(() => ({}));

        if (!isActive || requestId !== hybridRequestIdRef.current) return;

        if (!response.ok) {
          console.error("Hybrid item search failed", payload);
          setHybridItemsError("Could not load item matches right now.");
          setHybridItems([]);
        } else {
          setHybridItems(Array.isArray(payload?.items) ? payload.items : []);
        }
      } catch (err) {
        if (!isActive || requestId !== hybridRequestIdRef.current) return;
        if (err?.name === "AbortError") {
          logCrashEvent({
            context: "hybrid-search",
            kind: "timeout",
            message: "Hybrid search query timed out after 12s",
          });
          setHybridItemsError("Search is taking too long. Please try again.");
        } else {
          console.error("Hybrid item search threw", err);
          setHybridItemsError("Could not load item matches right now.");
        }
        setHybridItems([]);
      } finally {
        clearTimeout(timeoutId);
        if (isActive && requestId === hybridRequestIdRef.current) {
          setHybridItemsLoading(false);
          logDataDiag("request:finish", {
            label: "home:hybrid-search",
            requestId,
          });
        }
      }
    };

    loadHybridItems();

    return () => {
      isActive = false;
    };
  }, [search, logCrashEvent, categoryFilter, hasLocation, homeContentHydrated]);

  if (!isPublicMode && loadingUser && !user) {
    return (
      <div className={`min-h-screen ${textTone.base} relative px-6 pt-3`}>
        <div className="absolute inset-0 -z-10 overflow-hidden">
          <div className="absolute inset-0 bg-[#05010d]" />
          <div className="absolute inset-0 bg-gradient-to-b from-purple-900/40 via-fuchsia-900/30 to-black" />
          <div className="pointer-events-none absolute -top-32 -left-24 h-[420px] w-[420px] rounded-full bg-purple-600/30 blur-[120px]" />
          <div className="pointer-events-none absolute top-40 -right-24 h-[480px] w-[480px] rounded-full bg-pink-500/30 blur-[120px]" />
        </div>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center space-y-3">
            <YourBarrioLoader size="md" className="mx-auto text-white/80" />
            <p className={`text-lg ${textTone.muted}`}>Loading your account...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <section
      className={`relative w-full min-h-screen ${textTone.base} pt-0 md:pt-0 mt-0`}
      data-clickdiag={clickDiagEnabled ? "home" : undefined}
    >

      {/* Background */}
      <div className="absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute inset-0 bg-[#05010d]" />
        <div className="absolute inset-0 bg-gradient-to-b from-purple-900/40 via-fuchsia-900/30 to-black" />
        <div className="pointer-events-none absolute -top-32 -left-24 h-[420px] w-[420px] rounded-full bg-purple-600/30 blur-[120px]" />
        <div className="pointer-events-none absolute top-40 -right-24 h-[480px] w-[480px] rounded-full bg-pink-500/30 blur-[120px]" />
      </div>

      <div
        className="relative z-10 mx-auto w-full max-w-6xl px-6 after:pointer-events-none after:absolute after:inset-x-0 after:bottom-0 after:h-8 after:bg-gradient-to-b after:from-transparent after:to-white/10 md:px-8 md:after:h-10"
        data-home-content="1"
      >
        <div className="w-full max-w-none">
          {showLocationEmpty ? (
            <div className="mb-4 rounded-2xl border border-white/12 bg-white/5 backdrop-blur-xl p-4 text-sm text-white/70">
              Select a location to see listings near you.
            </div>
          ) : null}
          {authReady ? (
            <>
              {search ? (
                <div className="rounded-2xl border border-white/12 bg-white/5 backdrop-blur-xl shadow-xl px-4 py-4">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className={`text-[10px] uppercase tracking-[0.22em] ${textTone.subtle}`}>
                        AI picks
                      </p>
                      <p className="text-lg font-semibold">
                        Items matching “{search}”
                      </p>
                    </div>
                    <div className={`flex items-center gap-2 text-xs ${textTone.soft}`}>
                      {hybridItemsLoading ? (
                        <>
                          <YourBarrioLoader size="xs" />
                          <span>Scanning listings</span>
                        </>
                      ) : (
                        <>
                          <span className="inline-block h-2 w-2 rounded-full bg-emerald-400" />
                          <span>{hybridItems.length} item hits</span>
                        </>
                      )}
                    </div>
                  </div>

                  {hybridItemsError ? (
                    <div className="mt-3 text-sm text-rose-200">
                      {hybridItemsError}
                    </div>
                  ) : null}

                  {!hybridItemsLoading && !hybridItemsError && hybridItems.length === 0 ? (
                    <div className={`mt-3 text-sm ${textTone.soft}`}>
                      No items yet. Try a category like “electronics”, “home decor”, or “toys”.
                    </div>
                  ) : null}

                  <div className="grid sm:grid-cols-2 gap-3 mt-3">
                    {sortedHybridItems.map((item, idx) => {
                      const inventory = normalizeInventory(item);
                      const badgeStyle = getAvailabilityBadgeStyle(
                        inventory.availability,
                        isLight
                      );
                      const displayPrice = formatListingDisplayPrice(item);
                      const listingHref = `${isPublicMode ? "/listings" : "/customer/listings"}/${item.id}`;
                      return (
                      <Link
                        key={item.id}
                        href={listingHref}
                        prefetch={false}
                        className="group rounded-xl border border-white/12 bg-white/5 hover:border-white/30 hover:bg-white/10 transition overflow-hidden flex gap-3 pointer-events-auto touch-manipulation"
                        data-safe-nav="1"
                        data-clickdiag={clickDiagEnabled ? "tile" : undefined}
                        data-clickdiag-tile-id={clickDiagEnabled ? item.id : undefined}
                        data-clickdiag-bound={clickDiagEnabled ? "tile" : undefined}
                        onClickCapture={diagTileClick("REACT_TILE_CAPTURE", item.id || idx)}
                        onClick={diagTileClick("REACT_TILE_BUBBLE", item.id || idx)}
                      >
                        {coverFor(item) ? (
                          <FastImage
                            src={coverFor(item)}
                            alt={item.title || "Listing"}
                            className="h-20 w-20 rounded-lg border border-white/10 bg-white object-contain object-center"
                            fallbackSrc="/business-placeholder.png"
                            width={80}
                            height={80}
                            sizes="80px"
                            priority={idx < 2}
                            decoding="async"
                          />
                        ) : (
                          <div className={`h-20 w-20 rounded-lg bg-white/10 border border-white/10 flex items-center justify-center text-[11px] ${textTone.subtle}`}>
                            No image
                          </div>
                        )}
                        <div className="flex-1 pr-2 py-2">
                          <div className="flex flex-wrap items-start justify-between gap-2">
                            <div className="text-sm font-semibold leading-snug">
                              {item.title}
                            </div>
                            {displayPrice ? (
                              <div className={`text-sm font-semibold ${textTone.strong}`}>
                                {displayPrice}
                              </div>
                            ) : null}
                          </div>
                          <div className={`text-[11px] uppercase tracking-wide ${textTone.faint} mt-1`}>
                            {getListingCategoryLabel(item, "Listing")}
                            {item.city ? ` · ${item.city}` : ""}
                          </div>
                          <span
                            className="mt-2 inline-flex items-center rounded-full border bg-transparent px-2 py-1 text-[10px] font-semibold"
                            style={
                              badgeStyle
                                ? { color: badgeStyle.color, borderColor: badgeStyle.border }
                                : undefined
                            }
                          >
                            {inventory.label}
                          </span>
                          {item.description ? (
                            <p className={`text-xs ${textTone.soft} mt-1 line-clamp-2`}>
                              {item.description}
                            </p>
                          ) : null}
                        </div>
                      </Link>
                      );
                    })}
                  </div>
                </div>
              ) : null}

            </>
          ) : (
            <div className="space-y-4">
              <div className="h-12 w-32 rounded-full bg-white/5 border border-white/10" />
              <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl shadow-xl p-4 space-y-3">
                <div className="h-4 w-48 rounded bg-white/10" />
                <div className="h-4 w-64 rounded bg-white/10" />
                <div className="h-4 w-40 rounded bg-white/10" />
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
                <div className="lg:col-span-3 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl shadow-xl h-[240px]" />
                <div className="lg:col-span-2 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl shadow-xl h-[240px]" />
              </div>
            </div>
          )}
        </div>

      </div>
      {!search ? (
        <>
          <BusinessStoriesRow
            groups={storyGroups}
            mode={mode}
            location={homeContentHydrated ? location : { city: initialCity, region: initialRegion }}
            nextCursor={storyNextCursor}
            hasMore={storyHasMore}
            progressive
          />
          {showCityAvailabilityNotice ? (
            <HomepageAvailabilityNotice
              city={activeCity || null}
              href={launchListingsHref}
              onBrowseLaunch={browseLaunchListings}
            />
          ) : (
            <TrendingListingsSection
              mode={mode}
              listings={visibleHomeListings}
              city={activeCity || null}
              limit={4}
            />
          )}
          <HomepageEditorialSections
            mode={mode}
            listings={visibleHomeListings}
          />
          <div
            className="w-full bg-[#eadfd5] pt-6 md:pt-7"
            data-testid="homepage-lower-beige-canvas"
          >
            <SellerCTASection />
            <FeedbackSection mode={mode} />
          </div>
        </>
      ) : (
        <div className="w-full bg-[#eadfd5]">
          <FeedbackSection mode={mode} />
        </div>
      )}
    </section>
  );
}

export default function CustomerHomeClient({
  mode = "customer",
  featuredCategories: _featuredCategories,
  featuredCategoriesError: _featuredCategoriesError,
  initialListings = [],
  initialStoryGroups = [],
  initialStoryNextCursor = null,
  initialStoryHasMore = false,
  initialCity = null,
  initialRegion = null,
}) {
  const safeNavFlag = process.env.NEXT_PUBLIC_HOME_BISECT_SAFE_NAV === "1";
  const isPublicMode = mode === "public";
  const { theme, hydrated } = useTheme();
  const isLight = hydrated ? theme === "light" : true;
  const pageContent = (
    <>
      <GuaranteedNavCapture />
      <CustomerHomePageInner
        mode={mode}
        initialListings={initialListings}
        initialStoryGroups={initialStoryGroups}
        initialStoryNextCursor={initialStoryNextCursor}
        initialStoryHasMore={initialStoryHasMore}
        initialCity={initialCity}
        initialRegion={initialRegion}
      />
    </>
  );

  return (
    <CustomerHomeErrorBoundary isLight={isLight}>
      {safeNavFlag ? <SafeNavFallback /> : null}
      {isPublicMode ? (
        pageContent
      ) : (
        <HomeGuard fallback={<HomeGuardFallback />}>
          {pageContent}
        </HomeGuard>
      )}
    </CustomerHomeErrorBoundary>
  );
}
